import React from 'react';
import { FaMoon, FaSignOutAlt, FaSun } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

import useAuth from '../hooks/useAuth';
import { getPageTitle } from '../config/nav';
import { useTheme } from '../context/ThemeContext';
import { useRouteBreadcrumbs } from '../observability/useRouteBreadcrumbs';
import Container from '../ui/primitives/Container';
import BottomNav from './BottomNav';
import Sidebar from './Sidebar';

interface AppShellProps {
  children: React.ReactNode;
}

const AUTH_LAYOUT_ROUTES = new Set(['/login', '/register', '/signup', '/']);

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { pathname } = useLocation();
  useRouteBreadcrumbs();
  const pageTitle = getPageTitle(pathname);
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const isAuthLayout = AUTH_LAYOUT_ROUTES.has(pathname);

  if (isAuthLayout) {
    return (
      <div className="min-h-screen bg-bg text-text">
        <a
          href="#main-content"
          className="sr-only rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primaryFg focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:not-sr-only focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          Pular para o conteudo principal
        </a>
        <main
          id="main-content"
          role="main"
          tabIndex={-1}
          className="min-h-screen"
          aria-label="Main Content"
        >
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-text">
      <a
        href="#main-content"
        className="sr-only rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primaryFg focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:not-sr-only focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        Pular para o conteudo principal
      </a>
      <div className="md:grid md:min-h-screen md:grid-cols-[240px_1fr]">
        <Sidebar />

        <div className="flex min-h-screen flex-col">
          <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur">
            <Container className="flex h-16 items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
                  EcoVoz
                </p>
                <p
                  data-testid="app-shell-page-title"
                  className="truncate text-lg font-semibold text-text md:text-2xl"
                  aria-live="polite"
                >
                  {pageTitle}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm font-semibold text-text transition hover:bg-surface2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  aria-label={`Ativar tema ${theme === 'light' ? 'escuro' : 'claro'}`}
                >
                  {theme === 'light' ? (
                    <FaMoon aria-hidden="true" />
                  ) : (
                    <FaSun aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">
                    {theme === 'light' ? 'Escuro' : 'Claro'}
                  </span>
                </button>

                {isAuthenticated ? (
                  <button
                    type="button"
                    onClick={() => {
                      void logout();
                    }}
                    className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-danger/40 px-3 text-sm font-semibold text-danger transition hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  >
                    <FaSignOutAlt aria-hidden="true" />
                    <span className="hidden sm:inline">Sair</span>
                  </button>
                ) : null}
              </div>
            </Container>
          </header>

          <main
            id="main-content"
            role="main"
            tabIndex={-1}
            className="flex-1 pb-24 md:pb-8"
            aria-label="Main Content"
          >
            {children}
          </main>

          <BottomNav />
        </div>
      </div>
    </div>
  );
};

export default AppShell;
